// src/editor/SpecEditor.tsx
// The visual editor for a project's make_video spec: the beats, then
// panels for the background (zoom, badges), text style, music and end card.
// Every control edits the spec itself (no separate model), so the raw-JSON
// tab and this view always agree. Snippets: insert one (a style, music, end
// card or run of beats), or save a panel or the selected beats as one.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AssetSummary } from '../assetsApi';
import { SNIPPET_KINDS, SNIPPET_KIND_LABELS, type Snippet, type SnippetKind } from '@ring/shared/snippets';
import { TEMPLATES } from '../preview/cards';
import { DEFAULTS, DEFAULT_IMAGE_SCALE, DEFAULT_OVERLAY, END_TEXT_Y, GRID, deepMerge, type Json } from '../preview/spec';
import { BUILTIN_IMAGES, BUILTIN_REF_PREFIX, parseBuiltinRef } from '@ring/shared/projects';
import { AssetSlot } from './AssetPicker';
import { CheckField, ColourField, NumberField, Panel, SelectField, TextField } from './fields';
import {
  addTextBox, applySnippet, beatStartTimes, insertBeats, moveBeat, removeTextBox, setIn, showcaseTime, type Path,
} from './specOps';

const ANCHORS = ['top', 'middle', 'bottom'] as const;
const TEXT_STYLES = ['plain', 'shadow', 'pill'] as const;
/** What "+ Kindle Unlimited badge" adds: tucked into the top right corner, about a third of the width. */
const KU_BADGE: Json = { file: `${BUILTIN_REF_PREFIX}ku-black`, x: 98, y: 1.5, anchor: 'upper-right', scale: 0.35 };
const SIZES: [string, string][] = [['1080x1920', '9:16 (Reels, Stories)'], ['1080x1350', '4:5 (feed)'], ['1080x1080', '1:1 (square)']];
/** Templates that are text-card styles (kindle.html is the Kindle page). */
const STYLE_TEMPLATES = Object.keys(TEMPLATES).filter(t => t !== 'kindle').sort();

interface Props {
  spec:        Json;
  onChange:    (spec: Json) => void;
  assets:      AssetSummary[];
  assetsById:  Map<string, AssetSummary>;
  snippets:    Snippet[];
  onSaveSnippet:   (kind: SnippetKind, name: string, fragment: Json) => Promise<void>;
  onDeleteSnippet: (snippetId: string) => Promise<void>;
  /** The beat the preview is on (-1: the end card). */
  currentBeat: number;
  onPlayFrom:  (t: number) => void;
  /** Show this moment in the preview, if it's paused (for moving into a beat or the end card). */
  onShow:      (t: number) => void;
}

export function SpecEditor({ spec, onChange, assets, assetsById, snippets, onSaveSnippet, onDeleteSnippet, currentBeat, onPlayFrom, onShow }: Props) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState<{ kind: SnippetKind; fragment: Json; name: string; error?: string; busy?: boolean } | null>(null);

  /** The spec with defaults filled in, for showing what an empty field inherits. */
  const eff = useMemo(() => deepMerge(DEFAULTS, spec), [spec]);
  const st = eff.style as Json;
  const beats: Json[] = Array.isArray(spec.beats) ? spec.beats : [];
  const overlays: Json[] = Array.isArray(spec.background?.overlays) ? spec.background.overlays : [];
  const { starts, endStart } = beatStartTimes(spec);

  const set = (path: Path, v: unknown) => onChange(setIn(spec, path, v));
  /** Edits that change which index a beat is at drop the selection, rather than leave it on other beats. */
  const restructure = (next: Json) => { setSelected(new Set()); onChange(next); };

  function startSaving(kind: SnippetKind, fragment: Json) {
    setSaving({ kind, fragment: structuredClone(fragment), name: '' });
  }

  async function finishSaving() {
    if (!saving || !saving.name.trim()) return;
    setSaving({ ...saving, busy: true, error: undefined });
    try {
      await onSaveSnippet(saving.kind, saving.name.trim(), saving.fragment);
      setSaving(null);
    } catch (err) {
      setSaving({ ...saving, busy: false, error: err instanceof Error ? err.message : 'Couldn\'t save the snippet.' });
    }
  }

  function insert(s: Snippet) {
    // Beats go after the selection, else after the beat being previewed, else at the end
    const at = selected.size ? Math.max(...selected) + 1 : currentBeat >= 0 ? currentBeat + 1 : beats.length;
    restructure(applySnippet(spec, s, at));
  }

  const showEnd = () => { if (eff.endCard.image && currentBeat !== -1) onShow(showcaseTime(spec, -1)); };

  const sizeKey = Array.isArray(spec.size) ? spec.size.join('x') : '1080x1920';

  return (
    <div className="editor">
      <div className="editor-toolbar">
        <label className="editor-field">
          <span>Video size</span>
          <select value={sizeKey} onChange={e => set(['size'], e.target.value.split('x').map(Number))}>
            {SIZES.some(([k]) => k === sizeKey) ? null : <option value={sizeKey}>{sizeKey.replace('x', ' × ')}</option>}
            {SIZES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
        <SelectField label="Frame rate" value={spec.fps === undefined ? undefined : String(spec.fps)}
          options={[['24', '24 fps'], ['30', '30 fps'], ['60', '60 fps']]} inherit="30 fps (default)"
          onChange={v => set(['fps'], v === undefined ? undefined : Number(v))} />
        <SnippetMenu snippets={snippets} onInsert={insert} onDelete={onDeleteSnippet} />
      </div>

      {saving && (
        <form className="editor-save-snippet" onSubmit={e => { e.preventDefault(); void finishSaving(); }}>
          <span>Save {SNIPPET_KIND_LABELS[saving.kind].toLowerCase()}{saving.kind === 'beats' ? ` (${(saving.fragment.beats as Json[]).length})` : ''} as a snippet:</span>
          <input type="text" autoFocus placeholder="Name" maxLength={100} value={saving.name}
            onChange={e => setSaving({ ...saving, name: e.target.value })} />
          <button type="submit" className="projects-primary editor-small" disabled={saving.busy || !saving.name.trim()}>Save</button>
          <button type="button" className="projects-secondary editor-small" onClick={() => setSaving(null)}>Cancel</button>
          {saving.error && <span className="page-error-text">{saving.error}</span>}
        </form>
      )}

      <Panel title={`Beats (${beats.length})`} actions={<>
        {selected.size > 0 && (
          <button type="button" className="editor-text-btn"
            onClick={() => startSaving('beats', { beats: [...selected].sort((a, b) => a - b).map(i => beats[i]) })}>
            Save {selected.size} as snippet
          </button>
        )}
        <button type="button" className="editor-text-btn"
          onClick={() => restructure(insertBeats(spec, beats.length, [{ text: '', duration: 2.5 }]))}>+ Beat</button>
      </>}>
        <ol className="editor-beats">
          {beats.map((beat, i) => (
            <BeatRow key={i} index={i} beat={beat} start={starts[i]} count={beats.length} style={st}
              active={currentBeat === i} selected={selected.has(i)}
              assets={assets} assetsById={assetsById}
              onSelect={on => setSelected(prev => { const next = new Set(prev); if (on) next.add(i); else next.delete(i); return next; })}
              set={(path, v) => set(['beats', i, ...path], v)}
              replace={b => onChange(setIn(spec, ['beats', i], b))}
              onPlay={() => onPlayFrom(starts[i])}
              onEnter={() => { if (currentBeat !== i) onShow(showcaseTime(spec, i)); }}
              onMove={to => restructure(moveBeat(spec, i, to))}
              onDuplicate={() => restructure(insertBeats(spec, i + 1, [beat]))}
              onDelete={() => restructure(setIn(spec, ['beats', i], undefined))}
              onAddBox={() => onChange(addTextBox(spec, i))}
              onRemoveBox={j => onChange(removeTextBox(spec, i, j))} />
          ))}
        </ol>
        {beats.length === 0 && <p className="projects-sub">No beats yet. Add one to start.</p>}
      </Panel>

      <Panel title="Background">
        <AssetSlot label="Background" value={spec.background?.file} assets={assets} assetsById={assetsById}
          kinds={['image', 'video']} roles={['background']} allowNone={false}
          onChange={v => set(['background', 'file'], v)} />
        <div className="editor-row">
          <div className="editor-field">
            <span>Zoom towards</span>
            <ZoomGrid label="Zoom towards" value={eff.background.zoom.target} onChange={v => set(['background', 'zoom', 'target'], v)} />
          </div>
          <NumberField label="Zoom from" value={spec.background?.zoom?.from} inherited={1} min={1} step={0.05}
            onChange={v => set(['background', 'zoom', 'from'], v)} />
          <NumberField label="to" value={spec.background?.zoom?.to} inherited={1.25} min={1} step={0.05}
            onChange={v => set(['background', 'zoom', 'to'], v)} />
          <SelectField label="Easing" value={spec.background?.zoom?.ease} options={[['linear', 'Steady'], ['ease-out', 'Slowing down']]}
            inherit="Steady (default)" onChange={v => set(['background', 'zoom', 'ease'], v)} />
          <NumberField label="Darken" value={spec.style?.dim} inherited={DEFAULTS.style.dim} min={0} max={1} step={0.05}
            title="Darkens the background so text stands out: 0 = none, 1 = black" onChange={v => set(['style', 'dim'], v)} />
        </div>
        {overlays.map((o, j) => (
          <OverlayFields key={j} overlay={o} assets={assets} assetsById={assetsById}
            set={(field, v) => set(['background', 'overlays', j, field], v)}
            replace={next => set(['background', 'overlays', j], next)}
            onRemove={() => set(['background', 'overlays'], overlays.length > 1 ? overlays.filter((_, k) => k !== j) : undefined)} />
        ))}
        <div className="editor-beat-add">
          <button type="button" className="editor-text-btn"
            onClick={() => set(['background', 'overlays'], [...overlays, { ...KU_BADGE }])}>
            + Kindle Unlimited badge
          </button>
          <button type="button" className="editor-text-btn" onClick={() => set(['background', 'overlays'], [...overlays, { file: '' }])}>
            + Badge or logo from library
          </button>
        </div>
      </Panel>

      <Panel title="Text style" actions={
        <button type="button" className="editor-text-btn" onClick={() => startSaving('style', { style: spec.style ?? {} })}>Save as snippet</button>
      }>
        <div className="editor-row">
          <SelectField label="Font" value={spec.style?.template} options={STYLE_TEMPLATES} inherit="default"
            onChange={v => set(['style', 'template'], v)} />
          <SelectField label="Look" value={spec.style?.textStyle} options={TEXT_STYLES} inherit="shadow (default)"
            onChange={v => set(['style', 'textStyle'], v)} />
          <ColourField label="Colour" value={spec.style?.color} inherited={DEFAULTS.style.color} onChange={v => set(['style', 'color'], v)} />
          <ColourField label="Emphasis colour" value={spec.style?.accent} inherited={DEFAULTS.style.accent} onChange={v => set(['style', 'accent'], v)} />
          {st.textStyle === 'pill' && (
            <ColourField label="Pill colour" value={spec.style?.pillColor} inherited={DEFAULTS.style.pillColor} onChange={v => set(['style', 'pillColor'], v)} />
          )}
        </div>
        <div className="editor-row">
          <NumberField label="Height (y %)" value={spec.style?.y} inherited={50} min={0} max={100}
            title="Where text sits: 0 = top of the video, 100 = bottom" onChange={v => set(['style', 'y'], v)} />
          <SelectField label="Anchored by its" value={spec.style?.anchor} options={ANCHORS} inherit="middle (default)"
            onChange={v => set(['style', 'anchor'], v)} />
          <NumberField label="Font size" value={spec.style?.fontSize} inherited="auto" min={8} step={2}
            title="Empty = shrink to fit. A number is px on a 1080-wide video." onChange={v => set(['style', 'fontSize'], v)} />
          <NumberField label="Width %" value={spec.style?.width} inherited={100} min={40} max={100} step={5}
            onChange={v => set(['style', 'width'], v)} />
          <NumberField label="Fade (s)" value={spec.style?.fade} inherited={0.25} min={0} step={0.05}
            onChange={v => set(['style', 'fade'], v)} />
          <CheckField label="Keep clear of the app's buttons" value={spec.style?.safeZone} inherited
            onChange={v => set(['style', 'safeZone'], v)} />
        </div>
      </Panel>

      <Panel title="Music" actions={
        <button type="button" className="editor-text-btn" onClick={() => startSaving('audio', { audio: spec.audio ?? {} })}>Save as snippet</button>
      }>
        <AssetSlot label="Music" value={spec.audio?.file} assets={assets} assetsById={assetsById} kinds={['music']}
          onChange={v => set(['audio', 'file'], v)} />
        {spec.audio?.file ? (
          <div className="editor-row">
            <NumberField label="Start at (s)" value={spec.audio?.start} inherited={0} min={0} step={0.5}
              title="Seconds into the track" onChange={v => set(['audio', 'start'], v)} />
            <NumberField label="Fade in (s)" value={spec.audio?.fadeIn} inherited={0.3} min={0} step={0.1} onChange={v => set(['audio', 'fadeIn'], v)} />
            <NumberField label="Fade out (s)" value={spec.audio?.fadeOut} inherited={1} min={0} step={0.1} onChange={v => set(['audio', 'fadeOut'], v)} />
            <CheckField label="Level the volume (final render)" value={spec.audio?.loudnorm} onChange={v => set(['audio', 'loudnorm'], v)} />
          </div>
        ) : null}
      </Panel>

      <Panel title="End card" active={currentBeat === -1} actions={<>
        {spec.endCard && <button type="button" className="editor-text-btn" onClick={() => startSaving('endCard', { endCard: spec.endCard })}>Save as snippet</button>}
        {eff.endCard.image && <button type="button" className="editor-text-btn" onClick={() => onPlayFrom(endStart)}>▶ Play</button>}
      </>}>
        {/* Moving into the end card shows it (once it has an image), unless the preview is already on it */}
        <div onFocusCapture={showEnd} onPointerDownCapture={showEnd}>
          <EndCardFields spec={spec} eff={eff} set={set} assets={assets} assetsById={assetsById} />
        </div>
      </Panel>
    </div>
  );
}

// ── beats ────────────────────────────────────────────────────────

interface BeatRowProps {
  index: number; beat: Json; start: number; count: number; style: Json;
  active: boolean; selected: boolean;
  assets: AssetSummary[]; assetsById: Map<string, AssetSummary>;
  onSelect: (on: boolean) => void;
  set: (path: Path, v: unknown) => void;
  replace: (beat: Json) => void;
  /** Focus or a click arrived anywhere in this beat. */
  onEnter: () => void;
  onPlay: () => void; onMove: (to: number) => void; onDuplicate: () => void; onDelete: () => void;
  onAddBox: () => void; onRemoveBox: (j: number) => void;
}

function BeatRow({ index: i, beat, start, count, style, active, selected, assets, assetsById, onSelect, set, replace,
  onEnter, onPlay, onMove, onDuplicate, onDelete, onAddBox, onRemoveBox }: BeatRowProps) {
  const boxes: Json[] | null = Array.isArray(beat.texts) ? beat.texts : null;
  const images: Json[] = Array.isArray(beat.images) ? beat.images : [];
  return (
    <li className={`editor-beat${active ? ' editor-beat-active' : ''}${selected ? ' editor-beat-selected' : ''}`}
      onFocusCapture={onEnter} onPointerDownCapture={onEnter}>
      <div className="editor-beat-head">
        <input type="checkbox" checked={selected} onChange={e => onSelect(e.target.checked)} aria-label={`Select beat ${i + 1}`} />
        <span className="editor-beat-num">{i + 1}</span>
        <span className="editor-beat-time">{start.toFixed(1)}s</span>
        <NumberField label="Length" value={beat.duration} min={0.1} step={0.1} suffix="s" onChange={v => set(['duration'], v)} />
        <span className="editor-beat-tools">
          <button type="button" className="editor-icon-btn" title="Play from here" onClick={onPlay}>▶</button>
          <button type="button" className="editor-icon-btn" title="Move up" disabled={i === 0} onClick={() => onMove(i - 1)}>↑</button>
          <button type="button" className="editor-icon-btn" title="Move down" disabled={i === count - 1} onClick={() => onMove(i + 1)}>↓</button>
          <button type="button" className="editor-icon-btn" title="Duplicate" onClick={onDuplicate}>⧉</button>
          <button type="button" className="editor-icon-btn" title="Delete beat" onClick={onDelete}>✕</button>
        </span>
      </div>

      {boxes ? boxes.map((box, j) => (
        <div key={j} className="editor-box">
          <div className="editor-box-head">
            <span>Text box {j + 1}</span>
            <button type="button" className="editor-text-btn" onClick={() => onRemoveBox(j)}>Remove</button>
          </div>
          <TextBoxFields box={box} style={style} withStart onField={(f, v) => set(['texts', j, f], v)} />
        </div>
      )) : (
        <TextBoxFields box={beat} style={style} withStart={false} onField={(f, v) => set([f], v)} />
      )}

      {images.map((im, j) => (
        <ImageFields key={j} image={im} assets={assets} assetsById={assetsById}
          set={(path, v) => set(['images', j, ...path], v)}
          replace={next => set(['images', j], next)}
          onRemove={() => replace({ ...beat, images: images.filter((_, k) => k !== j) })} />
      ))}

      <div className="editor-beat-add">
        <button type="button" className="editor-text-btn" onClick={onAddBox}>+ Text box</button>
        <button type="button" className="editor-text-btn" onClick={() => set(['images'], [...images, { file: '' }])}>+ Image</button>
        <button type="button" className="editor-text-btn"
          onClick={() => set(['images'], [...images, { kindle: { text: 'Book text here.' }, anchor: 'top', y: 30 }])}>+ Kindle page</button>
      </div>
    </li>
  );
}

/** The fields of one text box: a beat with one text, a texts[] box, or the end-card text. */
function TextBoxFields({ box, style, withStart, onField, defaultY }: {
  box: Json; style: Json; withStart: boolean; onField: (field: string, v: unknown) => void; defaultY?: number;
}) {
  const [more, setMore] = useState(false);
  return (
    <div className="editor-textbox">
      <textarea rows={2} value={typeof box.text === 'string' ? box.text : ''} placeholder="Text (blank = just the background)"
        onChange={e => onField('text', e.target.value)} />
      <div className="editor-row">
        <NumberField label="Height (y %)" value={box.y} inherited={defaultY ?? style.y} min={0} max={100} onChange={v => onField('y', v)} />
        <SelectField label="Anchored by its" value={box.anchor} options={ANCHORS} inherit={`${style.anchor} (style)`} onChange={v => onField('anchor', v)} />
        {withStart && <NumberField label="Appears at (s)" value={box.start} inherited={0} min={0} step={0.1} onChange={v => onField('start', v)} />}
        <CheckField label="Emphasis" value={box.emphasis} onChange={v => onField('emphasis', v)} />
        <button type="button" className="editor-text-btn" onClick={() => setMore(m => !m)}>{more ? 'Less' : 'More…'}</button>
      </div>
      {more && (
        <div className="editor-row">
          <SelectField label="Look" value={box.textStyle} options={TEXT_STYLES} inherit={`${style.textStyle} (style)`} onChange={v => onField('textStyle', v)} />
          <NumberField label="Font size" value={box.fontSize} inherited={style.fontSize} min={8} step={2} onChange={v => onField('fontSize', v)} />
          <NumberField label="Width %" value={box.width} inherited={style.width} min={40} max={100} step={5} onChange={v => onField('width', v)} />
          {(box.textStyle ?? style.textStyle) === 'pill' && (
            <ColourField label="Pill colour" value={box.pillColor} inherited={style.pillColor} onChange={v => onField('pillColor', v)} />
          )}
          <TextField label="CSS" value={box.css} wide title="Inline CSS for the whole text, e.g. color:#8fd3ff; font-style:italic"
            inherited="e.g. font-style:italic" onChange={v => onField('css', v)} />
        </div>
      )}
    </div>
  );
}

function ImageFields({ image, assets, assetsById, set, replace, onRemove }: {
  image: Json; assets: AssetSummary[]; assetsById: Map<string, AssetSummary>;
  set: (path: Path, v: unknown) => void; replace: (im: Json) => void; onRemove: () => void;
}) {
  const kindle = image.kindle && typeof image.kindle === 'object' ? image.kindle as Json : null;
  return (
    <div className="editor-box">
      <div className="editor-box-head">
        <span>{kindle ? 'Kindle page' : 'Image'}</span>
        <button type="button" className="editor-text-btn" onClick={onRemove}>Remove</button>
      </div>
      {kindle ? <KindleFields kindle={kindle} set={(f, v) => set(['kindle', f], v)} /> : (
        <AssetSlot label="Image" value={image.file} assets={assets} assetsById={assetsById} kinds={['image']}
          roles={['character', 'cover', 'cover-3d', 'logo', 'badge', 'kindle-page']} allowNone={false}
          onChange={v => replace({ ...image, file: v })} />
      )}
      <div className="editor-row">
        <NumberField label="Size (% of width)" value={typeof image.scale === 'number' ? Math.round(image.scale * 100) : undefined}
          inherited={DEFAULT_IMAGE_SCALE * 100} min={1} max={100} step={5} onChange={v => set(['scale'], v === undefined ? undefined : v / 100)} />
        <NumberField label="Height (y %)" value={image.y} inherited={50} min={0} max={100} onChange={v => set(['y'], v)} />
        <SelectField label="Anchored by its" value={image.anchor} options={ANCHORS} inherit="middle" onChange={v => set(['anchor'], v)} />
        <NumberField label="Appears at (s)" value={image.start} inherited={0} min={0} step={0.1} onChange={v => set(['start'], v)} />
      </div>
    </div>
  );
}

function KindleFields({ kindle, set }: { kindle: Json; set: (field: string, v: unknown) => void }) {
  const highlights: unknown[] = Array.isArray(kindle.highlight) ? kindle.highlight : [];
  const phrases = highlights.map(h => (typeof h === 'string' ? h : String((h as Json)?.text ?? '')));
  return (
    <>
      <textarea rows={3} value={typeof kindle.text === 'string' ? kindle.text : ''} placeholder="The page's text"
        onChange={e => set('text', e.target.value)} />
      <div className="editor-row">
        <TextField label="Title" value={kindle.title} onChange={v => set('title', v)} />
        <SelectField label="Theme" value={kindle.theme} options={['light', 'sepia', 'dark']} inherit="light" onChange={v => set('theme', v)} />
        <NumberField label="Font size" value={kindle.fontSize} inherited="auto" min={8} step={2} onChange={v => set('fontSize', v)} />
        <NumberField label="Height ÷ width" value={kindle.aspect} inherited={1.2} min={0.3} max={3} step={0.1} onChange={v => set('aspect', v)} />
      </div>
      <LinesField label="Highlight (one phrase per line, copied exactly from the text)" lines={phrases} onChange={next => {
        // keep a phrase's colour if it already had one
        const items = next.map(line => highlights.find(h => typeof h === 'object' && h !== null && (h as Json).text === line) ?? line);
        set('highlight', items.length ? items : undefined);
      }} />
    </>
  );
}

/**
 * One item per line. Blank lines stay in the box while typing (so Enter
 * starts a new line) but never reach the spec.
 */
function LinesField({ label, lines, onChange }: { label: string; lines: string[]; onChange: (lines: string[]) => void }) {
  const joined = lines.join('\n');
  const [text, setText] = useState(joined);
  useEffect(() => {
    setText(t => (nonEmptyLines(t).join('\n') === joined ? t : joined));
  }, [joined]);
  return (
    <label className="editor-field editor-field-wide">
      <span>{label}</span>
      <textarea rows={2} value={text} onChange={e => { setText(e.target.value); onChange(nonEmptyLines(e.target.value)); }} />
    </label>
  );
}

function nonEmptyLines(text: string): string[] {
  return text.split('\n').filter(line => line.trim() !== '');
}

// ── background and end card ──────────────────────────────────────

function ZoomGrid({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="editor-zoomgrid" role="radiogroup" aria-label={label}>
      {Object.keys(GRID).map(cell => (
        <button key={cell} type="button" role="radio" aria-checked={value === cell} title={cell}
          className={value === cell ? 'editor-zoomgrid-on' : ''} onClick={() => onChange(cell)} />
      ))}
    </div>
  );
}

/** Where a corner of the position grid puts an overlay: that point of it, inset from the frame's edges. */
function cornerPosition(cell: string): Json {
  const [ax, ay] = GRID[cell];
  const pos: Json = { x: [4, 50, 96][ax * 2], y: [15, 50, 80][ay * 2], anchor: cell };
  // Leave out whatever matches the default, so the spec stays short
  for (const k of ['x', 'y', 'anchor']) if (pos[k] === DEFAULT_OVERLAY[k]) delete pos[k];
  return pos;
}

/**
 * One background.overlays[] entry: a built-in Kindle Unlimited badge or a
 * library image, pinned over the background for the whole video.
 */
function OverlayFields({ overlay, assets, assetsById, set, replace, onRemove }: {
  overlay: Json; assets: AssetSummary[]; assetsById: Map<string, AssetSummary>;
  set: (field: string, v: unknown) => void; replace: (o: Json) => void; onRemove: () => void;
}) {
  const builtin = parseBuiltinRef(overlay.file);
  const o = { ...DEFAULT_OVERLAY, ...overlay };
  return (
    <div className="editor-box">
      <div className="editor-box-head">
        <span>{builtin ? 'Kindle Unlimited badge' : 'Badge or logo'}</span>
        <button type="button" className="editor-text-btn" onClick={onRemove}>Remove</button>
      </div>
      {builtin ? (
        <SelectField label="Version" value={builtin}
          options={Object.entries(BUILTIN_IMAGES).map(([id, b]) => [id, b.label] as [string, string])}
          onChange={v => { if (v) set('file', BUILTIN_REF_PREFIX + v); }} />
      ) : (
        <AssetSlot label="Image" value={overlay.file} assets={assets} assetsById={assetsById} kinds={['image']}
          roles={['badge', 'logo']} allowNone={false} onChange={v => set('file', v)} />
      )}
      <div className="editor-row">
        <div className="editor-field">
          <span>Position</span>
          <ZoomGrid label="Position" value={o.anchor} onChange={cell => {
            const next: Json = { ...overlay };
            delete next.x; delete next.y; delete next.anchor;
            replace({ ...next, ...cornerPosition(cell) });
          }} />
        </div>
        <NumberField label="Across (x %)" value={overlay.x} inherited={DEFAULT_OVERLAY.x} min={0} max={100}
          title="Where its anchor point sits: 0 = left edge, 100 = right edge" onChange={v => set('x', v)} />
        <NumberField label="Down (y %)" value={overlay.y} inherited={DEFAULT_OVERLAY.y} min={0} max={100}
          title="Where its anchor point sits: 0 = top, 100 = bottom" onChange={v => set('y', v)} />
        <NumberField label="Size (% of width)" value={typeof overlay.scale === 'number' ? Math.round(overlay.scale * 100) : undefined}
          inherited={Math.round(DEFAULT_OVERLAY.scale * 100)} min={5} max={100} step={5} onChange={v => set('scale', v === undefined ? undefined : v / 100)} />
        <NumberField label="Opacity" value={overlay.opacity} inherited={DEFAULT_OVERLAY.opacity} min={0} max={1} step={0.05}
          onChange={v => set('opacity', v)} />
        <CheckField label="Fade out for the end card" value={overlay.during === undefined ? undefined : overlay.during === 'beats'}
          onChange={v => set('during', v ? 'beats' : undefined)} />
      </div>
    </div>
  );
}

/** A colour name or hex as #rrggbb, via the browser's own colour parsing ('navy' → '#000080'); black if it isn't one. */
function toHex(colour: string): string {
  const ctx = document.createElement('canvas').getContext('2d')!;
  ctx.fillStyle = '#000000';
  ctx.fillStyle = /^[0-9a-f]{6}$/i.test(colour) ? `#${colour}` : colour;
  return /^#[0-9a-f]{6}$/i.test(ctx.fillStyle) ? ctx.fillStyle : '#000000';
}

/**
 * endCard.pad: a blurred copy of the cover, a solid colour, or none (the
 * video's own background keeps zooming behind the cover). The colour is
 * always written as #rrggbb, one of the forms make_video accepts. Older
 * specs may hold a colour name; it's shown as its hex equivalent.
 */
function PadField({ value, onChange }: { value: unknown; onChange: (v: string | undefined) => void }) {
  const pad = typeof value === 'string' ? value : 'blur';
  const kind = pad === 'blur' ? 'blur' : pad === 'none' ? 'none' : 'colour';
  const hex = kind === 'colour' ? toHex(pad) : '#000000';
  return (
    <div className="editor-field">
      <span>Background</span>
      <span className="editor-pad">
        <label className="editor-check" title="The video's own background keeps zooming behind the cover">
          <input type="radio" name="end-pad" checked={kind === 'none'} onChange={() => onChange('none')} />
          None
        </label>
        <label className="editor-check">
          <input type="radio" name="end-pad" checked={kind === 'blur'} onChange={() => onChange(undefined)} />
          Blur
        </label>
        <label className="editor-check">
          <input type="radio" name="end-pad" checked={kind === 'colour'} onChange={() => onChange(hex)} />
          Colour
        </label>
        <input type="color" value={hex} aria-label="Background colour" disabled={kind !== 'colour'}
          onChange={e => onChange(e.target.value)} />
      </span>
    </div>
  );
}

function EndCardFields({ spec, eff, set, assets, assetsById }: {
  spec: Json; eff: Json; set: (path: Path, v: unknown) => void; assets: AssetSummary[]; assetsById: Map<string, AssetSummary>;
}) {
  const end: Json = spec.endCard ?? {};
  const e: Json = eff.endCard;
  const textRaw = end.text;
  const textBox: Json | null = textRaw === undefined ? null : typeof textRaw === 'string' ? { text: textRaw } : textRaw;

  /** endCard.text stays a plain string until it needs more than its text. */
  function setTextField(field: string, v: unknown) {
    const next: Json = { ...(textBox ?? { text: '' }) };
    if (v === undefined) delete next[field]; else next[field] = v;
    const keys = Object.keys(next);
    set(['endCard', 'text'], keys.length === 1 && keys[0] === 'text' ? next.text : next);
  }

  let fields: ReactNode = null;
  if (e.image) {
    fields = (
      <>
        <div className="editor-row">
          <SelectField label="Shown as" value={end.mode} options={[['overlay', 'Over the background'], ['fullscreen', 'Full screen']]}
            inherit="Over the background (default)" onChange={v => set(['endCard', 'mode'], v)} />
          <NumberField label="Length" value={end.duration} inherited={3} min={0.1} step={0.5} suffix="s" onChange={v => set(['endCard', 'duration'], v)} />
          {(e.mode !== 'fullscreen' || e.fit === 'contain') && (
            <NumberField label="Height (y %)" value={end.y} inherited={50} min={0} max={100} onChange={v => set(['endCard', 'y'], v)} />
          )}
          {e.mode !== 'fullscreen' ? (
            <NumberField label="Size (% of width)" value={typeof end.scale === 'number' ? Math.round(end.scale * 100) : undefined}
              inherited={80} min={1} max={100} step={5} onChange={v => set(['endCard', 'scale'], v === undefined ? undefined : v / 100)} />
          ) : (
            <>
              <SelectField label="Fit" value={end.fit} options={[['contain', 'Whole cover'], ['cover', 'Crop to fill']]}
                inherit="Whole cover (default)" onChange={v => set(['endCard', 'fit'], v)} />
              {e.fit === 'contain' && (
                <PadField value={end.pad} onChange={v => set(['endCard', 'pad'], v)} />
              )}
            </>
          )}
        </div>
        {textBox ? (
          <div className="editor-box">
            <div className="editor-box-head">
              <span>End card text</span>
              <button type="button" className="editor-text-btn" onClick={() => set(['endCard', 'text'], undefined)}>Remove</button>
            </div>
            <TextBoxFields box={textBox} style={eff.style} withStart={false} defaultY={END_TEXT_Y} onField={setTextField} />
          </div>
        ) : (
          <button type="button" className="editor-text-btn" onClick={() => set(['endCard', 'text'], 'Read it today')}>+ End card text</button>
        )}
      </>
    );
  }
  return (
    <>
      <AssetSlot label="Cover" value={end.image} assets={assets} assetsById={assetsById} kinds={['image']}
        roles={['cover', 'cover-3d']} onChange={v => set(['endCard', 'image'], v)} />
      {fields}
    </>
  );
}

// ── snippets ─────────────────────────────────────────────────────

function SnippetMenu({ snippets, onInsert, onDelete }: { snippets: Snippet[]; onInsert: (s: Snippet) => void; onDelete: (id: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  return (
    <div className="editor-snippets">
      <button type="button" className="projects-secondary editor-small" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        Insert snippet ▾
      </button>
      {open && (
        <div className="editor-snippet-menu" role="menu">
          {snippets.length === 0 && <p className="projects-sub">No snippets yet. Use “Save as snippet” on a panel, or select beats.</p>}
          {deleteError && <p className="page-error-text">{deleteError}</p>}
          {SNIPPET_KINDS.map(kind => {
            const list = snippets.filter(s => s.kind === kind);
            if (!list.length) return null;
            return (
              <div key={kind}>
                <div className="editor-snippet-kind">{SNIPPET_KIND_LABELS[kind]}</div>
                {list.map(s => (
                  <div key={s.snippetId} className="editor-snippet-item">
                    <button type="button" role="menuitem" className="editor-snippet-insert" onClick={() => { onInsert(s); setOpen(false); }}>
                      {s.name}{kind === 'beats' && ` (${(s.fragment.beats as unknown[]).length})`}
                    </button>
                    {confirm === s.snippetId ? (
                      <>
                        <button type="button" className="editor-text-btn editor-danger" onClick={() => {
                          setDeleteError(null);
                          onDelete(s.snippetId).then(() => setConfirm(null), err => {
                            setDeleteError(err instanceof Error ? err.message : 'Couldn\'t delete the snippet.');
                          });
                        }}>Delete</button>
                        <button type="button" className="editor-text-btn" onClick={() => setConfirm(null)}>Keep</button>
                      </>
                    ) : (
                      <button type="button" className="editor-icon-btn" title="Delete snippet" onClick={() => setConfirm(s.snippetId)}>✕</button>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
